import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { resources } from '../../core/services/resources';
import { extractJobErrorMessage } from '../../core/services/api';
import { DynamicMuiIcon } from '../../shared/components/DynamicMuiIcon';
import type { AiAssistantRole, DocumentCitation } from '../../core/types/data-models';

export type SectionAssistImpactLevel = 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH';

interface GenerateDocumentSectionJobResult {
  content: string;
  impactLevel: SectionAssistImpactLevel;
  impactSummary: string | null;
  citations: DocumentCitation[] | null;
}

// If the job hasn't resolved by then, give up waiting and let the user retry
// rather than leaving the dialog stuck with no feedback.
const JOB_TIMEOUT_MS = 2 * 60 * 1000;

const IMPACT_COLOR: Record<SectionAssistImpactLevel, 'default' | 'info' | 'warning' | 'error'> = {
  NONE: 'default',
  LOW: 'info',
  MEDIUM: 'warning',
  HIGH: 'error',
};

const MARKDOWN_SX = {
  fontSize: 'var(--font-size-sm)',
  '& p': { mt: 0, mb: 1 },
  '& p:last-child': { mb: 0 },
  '& ul, & ol': { pl: 2.5, mb: 1 },
} as const;

function SectionContentCard({
  title,
  content,
  selectable,
  selected,
  onSelect,
  impactLevel,
  impactSummary,
}: {
  title: string;
  content: string;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  impactLevel?: SectionAssistImpactLevel;
  impactSummary?: string | null;
}) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={selectable ? onSelect : undefined}
        sx={{
          px: selectable ? 0.5 : 1.5,
          py: 0.5,
          bgcolor: 'var(--color-surface-2)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          cursor: selectable ? 'pointer' : 'default',
        }}
      >
        {selectable && (
          <Checkbox
            size="small"
            checked={!!selected}
            onClick={(e) => {
              e.stopPropagation();
              onSelect?.();
            }}
          />
        )}
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        {impactLevel && (
          <Chip
            size="small"
            label={`Impact: ${impactLevel}`}
            color={IMPACT_COLOR[impactLevel]}
            variant={impactLevel === 'NONE' ? 'outlined' : 'filled'}
          />
        )}
      </Stack>
      {impactSummary && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, pt: 1 }}>
          {impactSummary}
        </Typography>
      )}
      <Box sx={{ p: 1.5, maxHeight: '32vh', overflowY: 'auto', ...MARKDOWN_SX }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </Box>
    </Box>
  );
}

export function AiAssistSectionDialog({
  open,
  onClose,
  sectionId,
  initialContent,
  role,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  sectionId: number;
  /** The edit textarea's current content — shown as the "Original section content" card and sent as the fixed baseline for every request in this session. */
  initialContent: string;
  role: AiAssistantRole | null;
  onApply: (content: string, impactLevel: SectionAssistImpactLevel, citations: DocumentCitation[] | null) => void;
}) {
  const [originalContent, setOriginalContent] = useState(initialContent);
  const [userPrompt, setUserPrompt] = useState('');
  const [jobId, setJobId] = useState<number | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [versions, setVersions] = useState<GenerateDocumentSectionJobResult[]>([]);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Reset all session state whenever the dialog is (re)opened, snapshotting
  // the original content so it stays fixed even if the caller's draft
  // changes later in the same editing session.
  useEffect(() => {
    if (open) {
      setOriginalContent(initialContent);
      setUserPrompt('');
      setJobId(null);
      setTimedOut(false);
      setSubmitError(null);
      setVersions([]);
      setSelectedVersionIndex(null);
    }
    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => clearTimer, []);

  const jobQuery = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => resources.getJob(jobId!),
    enabled: jobId != null && !timedOut,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'PENDING' || status === 'PROCESSING' ? 2000 : false;
    },
  });

  const jobStatus = jobQuery.data?.status;
  const running = jobId != null && !timedOut && (jobStatus == null || jobStatus === 'PENDING' || jobStatus === 'PROCESSING');

  useEffect(() => {
    if (jobId == null) return;
    if (jobStatus === 'COMPLETED') {
      clearTimer();
      try {
        const parsed = JSON.parse(jobQuery.data?.result ?? '') as GenerateDocumentSectionJobResult;
        if (!parsed?.content) throw new Error('empty content');
        setSelectedVersionIndex(versions.length);
        setVersions((prev) => [...prev, parsed]);
      } catch {
        setSubmitError('Failed to read the assistant’s response.');
      }
      setJobId(null);
    } else if (jobStatus === 'FAILED') {
      clearTimer();
      setSubmitError(jobQuery.data?.error ?? 'The assistant failed to generate content.');
      setJobId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatus, jobId]);

  const handleRun = async () => {
    if (!userPrompt.trim()) return;
    setSubmitError(null);
    try {
      const lastVersion = versions.length > 0 ? versions[versions.length - 1].content : undefined;
      const { jobId: newJobId } = await resources.generateDocumentSectionContent(sectionId, {
        userPrompt: userPrompt.trim(),
        originalContent,
        currentContent: lastVersion,
      });
      setTimedOut(false);
      clearTimer();
      timeoutRef.current = setTimeout(() => setTimedOut(true), JOB_TIMEOUT_MS);
      setJobId(newJobId);
      setUserPrompt('');
    } catch (err) {
      setSubmitError(extractJobErrorMessage(err));
    }
  };

  const handleApply = () => {
    if (selectedVersionIndex == null) return;
    const selected = versions[selectedVersionIndex];
    onApply(selected.content, selected.impactLevel, selected.citations);
  };

  const busy = running;
  const roleName = role?.name ?? 'AI Assistant';

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { sx: { minHeight: '70vh', maxHeight: '85vh' } } }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: role?.color || 'primary.main', width: 36, height: 36 }}>
            <DynamicMuiIcon iconName={role?.avatarIcon} sx={{ fontSize: 20 }} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {roleName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ask for help updating this section
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={onClose} disabled={busy}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
          <Stack spacing={2}>
            <SectionContentCard title="Original section content" content={originalContent} />
            {versions.map((version, idx) => (
              <SectionContentCard
                key={idx}
                title={`V${idx + 1}`}
                content={version.content}
                impactLevel={version.impactLevel}
                impactSummary={version.impactSummary}
                selectable
                selected={selectedVersionIndex === idx}
                onSelect={() => setSelectedVersionIndex(idx)}
              />
            ))}
          </Stack>
        </Box>

        <Box sx={{ flexShrink: 0, borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
          <Stack spacing={1.5}>
            {!busy && versions.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {roleName} would like to help you update this section. What would you like to change
                in the section content?
              </Typography>
            )}

            {busy && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  {roleName} is working on it…
                </Typography>
              </Stack>
            )}

            {submitError && <Alert severity="error">{submitError}</Alert>}
            {timedOut && !submitError && (
              <Alert severity="error">This is taking longer than expected. Please try again.</Alert>
            )}

            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                label={versions.length > 0 ? 'Refine further' : 'What would you like to change?'}
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                placeholder="e.g. Make the tone more formal and add a summary at the end…"
                multiline
                minRows={2}
                maxRows={3}
                fullWidth
                disabled={busy}
                autoFocus
              />
              <Button
                onClick={handleRun}
                disabled={busy || !userPrompt.trim()}
                variant="contained"
                sx={{ flexShrink: 0 }}
              >
                {busy ? 'Running…' : 'Run'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
        <Button onClick={handleApply} disabled={busy || selectedVersionIndex == null} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
